import type { ClockPort, TimerHandle } from '@lw/shared/clock';
import { RED_AFTER_MS } from '@lw/shared/constants';
import type { CommandType, MissionState, Precondition } from '@lw/shared/mission-fsm';
import type { PairView, RoleQuality } from '@lw/shared/indicator';
import type { Timeline } from '@lw/shared/time-sync';
import { ChronoClient, type ChronoEvent, type PongFrame, type TimeCacheStore } from './ChronoClient.js';
import { IndicatorController, type IndicatorTransition } from './IndicatorController.js';
import { Outbox, type Ack, type CommandEnvelope, type OutboxEvent, type OutboxRecord, type OutboxStore } from './Outbox.js';

/** The server snapshot as the client sees it (ARCHITECTURE §8.4); fields the UI reads. */
export interface ClientSnapshot {
  v: 1;
  missionId: string | null;
  seq: number;
  epochId: string;
  srvEpoch: number;
  state: MissionState;
  target: { type: 'MGRS' | 'NAME'; value: string } | null;
  expected: { minutes: number; startedAt: number } | null;
  solution: { laserCode: string; shellFlightTimeS: number; laserOnLeadS: number; gtl: { unit: string; value: number } | null } | null;
  solutionDraft: { laserCode: string; shellFlightTimeS: number; laserOnLeadS: number; gtl: { unit: string; value: number } | null } | null;
  timeline: (Timeline & { t0Meta: { method: string; uncMs: number; flags: string[]; reviewReason: string | null } }) | null;
  review: { t0: number; tLaserOn: number; tImpact: number; reason: string; tsRx: number } | null;
  seats: { UAV: { deviceId: string; user: string } | null; FDC: { deviceId: string; user: string } | null };
  uav: { ready: boolean; readyAt: number | null; readyStale: boolean; audioSelfTest: string | null; laserOnConfirmedAt: number | null; laserOffAt: number | null; laserOffTimeout: boolean; ceaseLasingAckAt: number | null };
  lastCancel: { band: string; at: number } | null;
  aarOpenedAt: number | null;
  markers: string[];
  links: Record<string, { conn: string; lastSeenAgoMs: number | null }>;
  flags: string[];
  minClientBuild: string;
}

export interface ClientEvent {
  seq: number;
  type: string;
  actorRole: string;
  actorUser: string | null;
  tEvent: number;
  payload: Record<string, unknown>;
  flags: string[];
  delayMs: number;
}

/** Minimal WebSocket surface (browser WebSocket or `ws`). */
export interface SocketLike {
  readonly readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onclose: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
}

export interface MissionCacheStore {
  load(): ClientSnapshot | null;
  save(s: ClientSnapshot): void;
}

export interface MissionClientOptions {
  clock: ClockPort;
  role: 'UAV' | 'FDC' | 'OBSERVER' | 'ADMIN';
  deviceId: string;
  user: string;
  bootId: string;
  /** base URL, e.g. https://laser.watch */
  baseUrl: string;
  wsUrl: string;
  openSocket: (url: string) => SocketLike;
  fetch: (url: string, init: { method: string; headers: Record<string, string>; body?: string; signal?: AbortSignal }) => Promise<{ ok: boolean; status: number; json(): Promise<unknown> }>;
  timeStore?: TimeCacheStore;
  outboxStore?: OutboxStore;
  missionCache?: MissionCacheStore;
  random?: () => number;
  clientBuild?: string;
  hidden?: () => boolean;
  online?: () => boolean;
  onEvent?: (e: { kind: 'chrono'; event: ChronoEvent } | { kind: 'outbox'; event: OutboxEvent } | { kind: 'indicator'; event: IndicatorTransition } | { kind: 'state'; snapshot: ClientSnapshot } | { kind: 'mission-event'; event: ClientEvent } | { kind: 'socket'; state: 'open' | 'closed' } | { kind: 'resync'; reason: string }) => void;
}

/**
 * The headless mission client (ARCHITECTURE §8.5–8.7): owns the ChronoClient, the Outbox and the
 * IndicatorController; applies snapshots iff `seq > local.seq`; RESUMEs on every (re)connect; fills
 * event gaps over REST; reconnects with backoff. Framework-free — the browser UI and the
 * integration harness drive it alike.
 */
export class MissionClient {
  readonly chrono: ChronoClient;
  readonly outbox: Outbox;
  readonly indicator: IndicatorController;
  snapshot: ClientSnapshot | null;
  pair: PairView | null = null;
  /** the other stations' link state as the server reports it to this recipient (C-4 filtered) */
  stations: Partial<Record<'UAV' | 'FDC' | 'OBSERVER', RoleQuality>> = {};
  stationsAt: number | null = null;
  readonly events: ClientEvent[] = [];
  private socket: SocketLike | null = null;
  private attached = false;
  private reconnectHandle: TimerHandle | null = null;
  private reconnects: number[] = [];
  private everOpened = false;
  private attempts = 0;
  private stopped = false;
  private lastEventSeq = 0;
  /** the next STATE frame answers our RESUME and is authoritative even if its seq is lower (e.g. a
   *  restored database reusing a mission id) */
  private resumePending = false;
  private readonly opts: MissionClientOptions;
  private readonly clock: ClockPort;

  constructor(opts: MissionClientOptions) {
    this.opts = opts;
    this.clock = opts.clock;
    this.snapshot = opts.missionCache?.load() ?? null;
    this.lastEventSeq = this.snapshot?.seq ?? 0;
    this.chrono = new ChronoClient(opts.clock, {
      send: (f) => this.sendRaw(JSON.stringify(f)),
      httpPing: async (f) => {
        const res = await opts.fetch(`${opts.baseUrl}/api/v1/time?t0=${f.t0}&seq=${f.seq}&device=${encodeURIComponent(opts.deviceId)}`, { method: 'GET', headers: this.headers() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return (await res.json()) as PongFrame;
      },
      ...(opts.timeStore ? { store: opts.timeStore } : {}),
      onEvent: (e) => opts.onEvent?.({ kind: 'chrono', event: e }),
      qExtra: () => ({ state: this.indicator.current().colour, hidden: opts.hidden?.() ?? false, reconnects300: this.reconnects300() }),
    });
    this.outbox = new Outbox(opts.clock, this.chrono, {
      deviceId: opts.deviceId,
      bootId: opts.bootId,
      ...(opts.clientBuild ? { clientBuild: opts.clientBuild } : {}),
      ...(opts.outboxStore ? { store: opts.outboxStore } : {}),
      ...(opts.random ? { random: opts.random } : {}),
      send: (env, timeoutMs, signal) => this.post(env, timeoutMs, signal),
      onEvent: (e) => opts.onEvent?.({ kind: 'outbox', event: e }),
      onAck: (ack) => this.applySnapshot((ack as Ack & { snapshot: ClientSnapshot }).snapshot),
    });
    this.indicator = new IndicatorController(opts.clock, this.chrono, {
      role: opts.role,
      wsOpen: () => this.attached,
      navigatorOnline: () => opts.online?.() ?? true,
      reconnects300: () => this.reconnects300(),
      mission: () => ({ state: this.snapshot?.state ?? 'IDLE', seats: { UAV: !!this.snapshot?.seats.UAV, FDC: !!this.snapshot?.seats.FDC } }),
      // the pair bound is only trustworthy while sync.quality is arriving; a frozen/backgrounded tab
      // must not keep reporting a stale GREEN peer (§7.2) — expire it so the indicator drops to PEER?
      pair: () => (this.peerFresh() ? this.pair : null),
      onTransition: (t) => opts.onEvent?.({ kind: 'indicator', event: t }),
    });
  }

  private watchdogHandle: TimerHandle | null = null;

  start(): void {
    this.stopped = false;
    this.chrono.start();
    this.indicator.start();
    this.connect();
    this.watchdog();
  }

  /**
   * A socket that claims to be open but has not delivered a pong for RED_AFTER_MS is dead for our
   * purposes (airplane mode, a NAT that silently dropped the flow): close it ourselves so the
   * reconnect loop runs, instead of waiting minutes for TCP to notice.
   */
  private watchdog(): void {
    if (this.stopped) return;
    const m = this.chrono.metrics();
    if (this.attached && this.socket && m.sincePongMs !== null && m.sincePongMs > RED_AFTER_MS) {
      this.opts.onEvent?.({ kind: 'resync', reason: `no pong for ${(m.sincePongMs / 1000).toFixed(1)} s — reconnecting` });
      this.socket.close();
    }
    this.watchdogHandle = this.clock.setTimeout(() => this.watchdog(), 1000);
  }

  stop(): void {
    this.stopped = true;
    if (this.reconnectHandle) this.clock.clearTimeout(this.reconnectHandle);
    this.reconnectHandle = null;
    if (this.watchdogHandle) this.clock.clearTimeout(this.watchdogHandle);
    this.watchdogHandle = null;
    this.chrono.stop();
    this.indicator.stop();
    this.outbox.stop();
    this.socket?.close();
    this.socket = null;
    this.attached = false;
  }

  /** visibility regain / online / pageshow */
  resume(): void {
    this.chrono.onResume();
    if (!this.attached) this.connect();
    else {
      this.resumePending = true;
      this.sendRaw(JSON.stringify({ type: 'RESUME', missionId: this.snapshot?.missionId ?? null, lastSeq: this.snapshot?.seq ?? -1 }));
    }
    this.outbox.flush();
  }

  isConnected(): boolean {
    return this.attached;
  }

  /** ms since the last sync.quality frame, or null if none received. */
  syncQualityAgeMs(): number | null {
    return this.stationsAt === null ? null : this.clock.perfNow() - this.stationsAt;
  }

  /** True while the peer/pair view is current (a fresh sync.quality within RED_AFTER_MS). */
  peerFresh(): boolean {
    const age = this.syncQualityAgeMs();
    return this.attached && age !== null && age < RED_AFTER_MS;
  }

  /** Queue a command (never gated on connectivity, §11.4). */
  send(type: CommandType, payload: Record<string, unknown> = {}, precondition?: Precondition): OutboxRecord {
    const role = this.opts.role === 'FDC' ? 'FDC' : 'UAV';
    const pre: Precondition = precondition !== undefined ? precondition : this.defaultPrecondition(type);
    return this.outbox.enqueue(type, this.snapshot?.missionId ?? '', role, payload, pre);
  }

  /** seq commands pin the exact seq; the rest pin the state (§6.2). */
  defaultPrecondition(type: CommandType): Precondition {
    const seqTypes: CommandType[] = ['CREATE_CFF', 'BACK', 'START_MISSION', 'SOLUTION_NEXT', 'PROGRAMMING_NEXT', 'LOADING_NEXT'];
    if (type === 'CREATE_CFF') return { basedOnSeq: 0 }; // a new mission starts at seq 0
    if (seqTypes.includes(type)) return { basedOnSeq: this.snapshot?.seq ?? 0 };
    return this.snapshot ? { expectedState: this.snapshot.state } : null;
  }

  // ── transport ────────────────────────────────────────────────────────────────────────────────

  private connect(): void {
    if (this.stopped || this.socket) return;
    const o = this.opts;
    const url = `${o.wsUrl}?device=${encodeURIComponent(o.deviceId)}&user=${encodeURIComponent(o.user)}&role=${o.role}`;
    let s: SocketLike;
    try {
      s = o.openSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = s;
    s.onopen = () => {
      this.attached = true;
      this.attempts = 0;
      // only successful re-opens count as reconnects (§7.1): a flapping link, not a long outage
      if (this.everOpened) this.reconnects.push(this.clock.perfNow());
      this.everOpened = true;
      o.onEvent?.({ kind: 'socket', state: 'open' });
      this.chrono.onOpen();
      this.resumePending = true;
      this.sendRaw(JSON.stringify({ type: 'RESUME', missionId: this.snapshot?.missionId ?? null, lastSeq: this.snapshot?.seq ?? -1 }));
      this.outbox.flush();
    };
    s.onmessage = (ev) => this.onMessage(String(ev.data));
    s.onerror = () => undefined;
    s.onclose = () => {
      const wasAttached = this.attached;
      this.attached = false;
      this.socket = null;
      this.chrono.onClose();
      if (wasAttached) o.onEvent?.({ kind: 'socket', state: 'closed' });
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectHandle) return;
    this.attempts += 1;
    const delay = Math.min(5000, 500 * Math.pow(2, Math.min(4, this.attempts - 1))) * (0.8 + 0.4 * (this.opts.random?.() ?? 0.5));
    this.reconnectHandle = this.clock.setTimeout(() => {
      this.reconnectHandle = null;
      this.connect();
    }, delay);
  }

  private reconnects300(): number {
    const now = this.clock.perfNow();
    this.reconnects = this.reconnects.filter((t) => now - t < 300_000);
    return this.reconnects.length;
  }

  private sendRaw(data: string): void {
    if (this.socket && this.socket.readyState === 1) this.socket.send(data);
  }

  private onMessage(raw: string): void {
    let msg: { type?: string } & Record<string, unknown>;
    try {
      msg = JSON.parse(raw) as typeof msg;
    } catch {
      return;
    }
    switch (msg.type) {
      case 'pong':
        this.chrono.onPong(msg as unknown as PongFrame);
        break;
      case 'sync.quality': {
        const p = msg['pair'] as { known: boolean; bound: number | null; ok: boolean };
        this.pair = { known: p.known, bound: p.bound, ok: p.ok };
        this.stations = (msg['roles'] as typeof this.stations) ?? {};
        this.stationsAt = this.clock.perfNow();
        this.chrono.onServerFrame(msg['serverTime'] as number);
        break;
      }
      case 'STATE':
        this.applySnapshot(msg['snapshot'] as ClientSnapshot, this.resumePending);
        this.resumePending = false;
        this.chrono.onServerFrame(msg['srvEpoch'] as number | undefined);
        break;
      case 'EVENT':
        this.onEventFrame(msg['event'] as ClientEvent, msg['missionId'] as string);
        break;
      case 'EVENT_GAP':
        void this.fetchEvents(msg['missionId'] as string, this.lastEventSeq);
        break;
      case 'WELCOME': {
        // a > 2 s disagreement with the cached anchor re-anchors immediately (§8.6 step 2)
        const srv = msg['serverTs'] as number;
        const mine = this.chrono.serverNow();
        if (mine !== null && Math.abs(mine - srv) > 2000) {
          this.chrono.est.invalidate('WELCOME_DISAGREES');
          this.opts.onEvent?.({ kind: 'resync', reason: `WELCOME disagrees by ${(srv - mine).toFixed(0)} ms` });
        }
        break;
      }
      default:
        break;
    }
  }

  /** Apply iff seq > local.seq (or a different mission). Idempotent, order-insensitive (§8.4). */
  applySnapshot(s: ClientSnapshot | undefined, authoritative = false): void {
    if (!s) return;
    const cur = this.snapshot;
    if (!authoritative && cur && cur.missionId === s.missionId && s.seq <= cur.seq) return;
    if (authoritative && cur && cur.missionId === s.missionId && s.seq === cur.seq) return;
    if (!cur || cur.missionId !== s.missionId) {
      this.events.length = 0;
      this.lastEventSeq = 0;
    }
    this.snapshot = s;
    this.opts.missionCache?.save(s);
    this.opts.onEvent?.({ kind: 'state', snapshot: s });
    this.outbox.revalidate({ missionId: s.missionId ?? '', seq: s.seq, state: s.state });
  }

  private onEventFrame(e: ClientEvent, missionId: string): void {
    if (this.snapshot && this.snapshot.missionId !== missionId) return;
    if (e.seq > this.lastEventSeq + 1) void this.fetchEvents(missionId, this.lastEventSeq);
    if (e.seq > this.lastEventSeq) {
      this.events.push(e);
      this.lastEventSeq = e.seq;
      this.opts.onEvent?.({ kind: 'mission-event', event: e });
    }
  }

  private async fetchEvents(missionId: string, since: number): Promise<void> {
    try {
      const res = await this.opts.fetch(`${this.opts.baseUrl}/api/v1/missions/${missionId}/events?sinceSeq=${since}`, { method: 'GET', headers: this.headers() });
      if (!res.ok) return;
      const body = (await res.json()) as { events: ClientEvent[] };
      for (const e of body.events) {
        if (e.seq > this.lastEventSeq) {
          this.events.push(e);
          this.lastEventSeq = e.seq;
          this.opts.onEvent?.({ kind: 'mission-event', event: e });
        }
      }
    } catch {
      // the next frame or poll self-heals
    }
  }

  private headers(): Record<string, string> {
    // x-lw-* is the Phase 3/4 dev identity; x-lw-client is the CSRF marker on state-changing
    // requests (§9.12). Under Phase 5 cookie sessions the identity headers are ignored.
    return { 'content-type': 'application/json', 'x-lw-client': 'web', 'x-lw-device': this.opts.deviceId, 'x-lw-user': this.opts.user, 'x-lw-role': this.opts.role };
  }

  private async post(env: CommandEnvelope, _timeoutMs: number, signal: AbortSignal): Promise<Ack> {
    const res = await this.opts.fetch(`${this.opts.baseUrl}/api/v1/missions/current/commands`, { method: 'POST', headers: { ...this.headers(), 'idempotency-key': env.cmdId }, body: JSON.stringify(env), signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as Ack;
  }
}
